import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const AdminManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSpanish } = useLanguage();
  const [admins, setAdmins] = useState([]);
  const [superAdminEmail, setSuperAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // New admin form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Reset password form
  const [resetEmail, setResetEmail] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.request('/api/super-admin/users');
      setAdmins(data.admins || []);
      setSuperAdminEmail(data.super_admin_email || '');
    } catch (err) {
      if (err.message?.includes('403') || err.message?.includes('Super admin')) {
        setError(isSpanish 
          ? 'Acceso denegado. Solo el super administrador puede acceder a esta página.'
          : 'Access denied. Only the super admin can access this page.');
      } else {
        setError(err.message || 'Failed to load admins');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setAddingAdmin(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.request('/api/super-admin/add-admin', {
        method: 'POST',
        body: JSON.stringify(newAdmin)
      });
      setSuccess(`Admin ${newAdmin.email} added successfully`);
      setNewAdmin({ email: '', password: '', name: '' });
      setShowAddForm(false);
      loadAdmins();
    } catch (err) {
      setError(err.message || 'Failed to add admin');
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !newPassword) return;
    
    setResettingPassword(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.request('/api/super-admin/reset-admin-password', {
        method: 'POST',
        body: JSON.stringify({ email: resetEmail, new_password: newPassword })
      });
      setSuccess(`Password reset for ${resetEmail}`);
      setResetEmail(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleToggleAdmin = async (email) => {
    setError(null);
    setSuccess(null);
    
    try {
      const result = await api.request(`/api/super-admin/toggle-admin/${encodeURIComponent(email)}`, {
        method: 'PUT'
      });
      setSuccess(result.message);
      loadAdmins();
    } catch (err) {
      setError(err.message || 'Failed to toggle admin status');
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!confirm(`Are you sure you want to remove admin privileges from ${email}?`)) return;
    
    setError(null);
    setSuccess(null);
    
    try {
      await api.request(`/api/super-admin/remove-admin/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      setSuccess(`Admin privileges removed from ${email}`);
      loadAdmins();
    } catch (err) {
      setError(err.message || 'Failed to remove admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-background">
        <Header />
        <main className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && error.includes('Access denied')) {
    return (
      <div className="min-h-dvh bg-background">
        <Header />
        <main className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="bento-item text-center py-16">
              <Icon name="ShieldX" size={64} color="#EF4444" className="mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {isSpanish ? 'Acceso Denegado' : 'Access Denied'}
              </h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button variant="default" onClick={() => navigate('/main-dashboard')}>
                {isSpanish ? 'Volver al Inicio' : 'Back to Dashboard'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isSpanish ? 'Gestión de Administradores' : 'Admin Management'} - Lumiveta</title>
      </Helmet>

      <div className="min-h-dvh bg-background">
        <Header />
        
        <main className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-4xl">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Button variant="ghost" onClick={() => navigate('/main-dashboard')}>
                  <Icon name="ArrowLeft" size={18} />
                </Button>
                <h1 className="font-heading font-bold text-3xl text-gold-gradient">
                  {isSpanish ? 'Gestión de Administradores' : 'Admin Management'}
                </h1>
              </div>
              <p className="text-muted-foreground ml-12">
                {isSpanish 
                  ? 'Gestiona administradores, establece contraseñas y permisos'
                  : 'Manage admins, set passwords, and permissions'}
              </p>
            </div>

            {/* Alerts */}
            {error && !error.includes('Access denied') && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="#EF4444" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={20} color="#10B981" />
                <p className="text-sm text-success">{success}</p>
              </div>
            )}

            {/* Super Admin Info */}
            <div className="bento-item mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Icon name="Crown" size={24} color="#FFD700" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{isSpanish ? 'Super Administrador' : 'Super Admin'}</p>
                  <p className="font-semibold text-foreground">{superAdminEmail}</p>
                </div>
              </div>
            </div>

            {/* Add Admin Button */}
            <div className="mb-6">
              <Button variant="default" onClick={() => setShowAddForm(!showAddForm)} className="gold-glow">
                <Icon name={showAddForm ? 'X' : 'UserPlus'} size={18} className="mr-2" />
                {showAddForm 
                  ? (isSpanish ? 'Cancelar' : 'Cancel')
                  : (isSpanish ? 'Agregar Nuevo Admin' : 'Add New Admin')}
              </Button>
            </div>

            {/* Add Admin Form */}
            {showAddForm && (
              <div className="bento-item mb-6">
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                  {isSpanish ? 'Agregar Nuevo Administrador' : 'Add New Administrator'}
                </h3>
                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {isSpanish ? 'Correo Electrónico' : 'Email'} *
                    </label>
                    <input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                      required
                      className="w-full p-3 rounded-lg bg-input border border-border text-foreground"
                      placeholder="admin@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {isSpanish ? 'Nombre' : 'Name'}
                    </label>
                    <input
                      type="text"
                      value={newAdmin.name}
                      onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                      className="w-full p-3 rounded-lg bg-input border border-border text-foreground"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {isSpanish ? 'Contraseña' : 'Password'} ({isSpanish ? 'opcional - crear cuenta' : 'optional - creates account'})
                    </label>
                    <input
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                      className="w-full p-3 rounded-lg bg-input border border-border text-foreground"
                      placeholder="••••••••"
                      minLength={6}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {isSpanish 
                        ? 'Si se proporciona, se creará una cuenta de usuario automáticamente'
                        : 'If provided, a user account will be created automatically'}
                    </p>
                  </div>
                  <Button type="submit" variant="default" disabled={addingAdmin}>
                    {addingAdmin ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                        {isSpanish ? 'Agregando...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <Icon name="UserPlus" size={18} className="mr-2" />
                        {isSpanish ? 'Agregar Admin' : 'Add Admin'}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            )}

            {/* Reset Password Modal */}
            {resetEmail && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setResetEmail(null)}>
                <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                    {isSpanish ? 'Restablecer Contraseña' : 'Reset Password'}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {isSpanish ? 'Para:' : 'For:'} <strong>{resetEmail}</strong>
                  </p>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {isSpanish ? 'Nueva Contraseña' : 'New Password'}
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full p-3 rounded-lg bg-input border border-border text-foreground"
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" fullWidth onClick={() => setResetEmail(null)}>
                        {isSpanish ? 'Cancelar' : 'Cancel'}
                      </Button>
                      <Button type="submit" variant="default" fullWidth disabled={resettingPassword}>
                        {resettingPassword ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Guardar' : 'Save')}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Admin List */}
            <div className="bento-item">
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                <Icon name="Users" size={20} color="#FFD700" />
                {isSpanish ? 'Administradores' : 'Administrators'} ({admins.length})
              </h3>
              
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div 
                    key={admin.email} 
                    className="flex items-center justify-between p-4 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${admin.is_super_admin ? 'bg-primary/20' : 'bg-muted'}`}>
                        <Icon 
                          name={admin.is_super_admin ? 'Crown' : 'User'} 
                          size={20} 
                          color={admin.is_super_admin ? '#FFD700' : '#94A3B8'} 
                        />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {admin.user_name || admin.email.split('@')[0]}
                          {admin.is_super_admin && (
                            <span className="ml-2 text-xs text-primary font-normal">(Super Admin)</span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                      </div>
                    </div>
                    
                    {!admin.is_super_admin && (
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setResetEmail(admin.email)}
                          title={isSpanish ? 'Restablecer contraseña' : 'Reset password'}
                        >
                          <Icon name="Key" size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveAdmin(admin.email)}
                          className="text-error hover:bg-error/10"
                          title={isSpanish ? 'Quitar admin' : 'Remove admin'}
                        >
                          <Icon name="UserMinus" size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                
                {admins.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="Users" size={40} color="#94A3B8" className="mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {isSpanish ? 'No hay administradores' : 'No administrators found'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminManagement;
